import { Button, Col, Row, Select, Space, Card, Typography } from "antd"
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { observer } from "mobx-react"
import { useContainerInjection } from "../../../hooks/useContainerInjection"
import { SurveyFormData, SurveysViewModel } from "../SurveysViewModel"
import { useEffect, useState } from "react"
import SurveyAnswersTable from "./SurveyAnswersTable"
import { SurveyAnswer } from "../../../../domain/entities/SurveyAnswer"
import { SurveySection } from "../../../../domain/entities/SurveySection"
import Switch from "../../../components/Switch/Switch"

const { Title, Text } = Typography
const { Option } = Select

interface SurveySectionsFormSectionProps {
	formData: SurveyFormData
	onChange: (formData: Partial<SurveyFormData>) => void
	answers?: SurveyAnswer[]
}

const SurveySectionsForm = ({ formData, onChange, answers }: SurveySectionsFormSectionProps) => {
	const { t } = useTranslation("surveys", { keyPrefix: "form.sections" })
	const { t: tAnswers } = useTranslation("surveys", { keyPrefix: "form.answers" })
	const viewModel = useContainerInjection<SurveysViewModel>("SurveysViewModel")
	const [selectedSectionId, setSelectedSectionId] = useState<number | undefined>()
	const [answersByQuestion, setAnswersByQuestion] = useState<Record<number, SurveyAnswer[]>>({})
	const [orderedSections, setOrderedSections] = useState<SurveySection[]>([])

	useEffect(() => {
		if (viewModel.surveySections.length === 0) {
			viewModel.fetchSurveySections()
		}
	}, [])

	useEffect(() => {
		setAnswersByQuestion(
			answers?.reduce(
				(acc, answer) => {
					acc[answer.questionId] = (acc[answer.questionId] ?? []).concat([answer])
					return acc
				},
				{} as Record<number, SurveyAnswer[]>
			) ?? {}
		)
	}, [answers])

	useEffect(() => {
		setOrderedSections(formData.sections?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) ?? [])
	}, [formData.sections])

	const availableSections = viewModel.surveySections.filter(
		section => !formData.sections?.some(s => s.id === section.id)
	)

	const addSection = () => {
		const section = viewModel.surveySections.find(s => s.id === selectedSectionId)
		if (!selectedSectionId || !section) return

		const newSections = [...(formData.sections || []), section]
		onChange({ sections: newSections })
		setSelectedSectionId(undefined)
	}

	const removeSection = (sectionId: number) => {
		const newSections = formData.sections?.filter(s => s.id !== sectionId) || []
		onChange({ sections: newSections })
	}

	return (
		<>
			<Row gutter={[24, 0]}>
				<Col span={24}>
					<Space direction="vertical" style={{ width: "100%" }} size="large">
						<div>
							<Title level={5}>{t("addSection")}</Title>
							<Space>
								<Select
									style={{ width: 300 }}
									placeholder={t("selectSectionPlaceholder")}
									value={selectedSectionId}
									onChange={setSelectedSectionId}
									loading={viewModel.isLoading}
								>
									{availableSections.map(section => (
										<Option key={`section-option-${section.id}`} value={section.id}>
											{section.name}
										</Option>
									))}
								</Select>
								<Button
									type="primary"
									icon={<PlusOutlined />}
									onClick={addSection}
									disabled={!selectedSectionId}
								>
									{t("addSection")}
								</Button>
							</Space>
						</div>
						{orderedSections?.map(section => {
							return (
								<Card
									key={`section-${section.id}`}
									title={
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center"
											}}
										>
											<Title level={5} style={{ margin: 0 }}>
												{section?.name}
											</Title>
											<Button
												type="text"
												icon={<DeleteOutlined />}
												onClick={() => removeSection(section.id!)}
												danger
											/>
										</div>
									}
									bodyStyle={{ padding: "16px" }}
								>
									<Space direction="vertical" style={{ width: "100%" }} size="small">
										<Text>{section?.description || t("noDescription")}</Text>
										{section.questions?.map(question => {
											const questionAnswers = answersByQuestion[question.id!] || []
											return (
												<Card
													key={`question-answers-${question.id}`}
													size="small"
													title={
														<div className="flex flex-col">
															<div className="flex flex-row justify-between">
																<Text strong>{question.text}</Text>
																<Switch
																	checked={question.enabled}
																	containerStyles={{ marginBottom: 0 }}
																	onChange={(value: boolean) =>
																		onChange &&
																		onChange({
																			sections: formData.sections?.map(
																				section => ({
																					...section,
																					questions: section.questions?.map(
																						q => ({
																							...q,
																							enabled:
																								q.id === question.id
																									? value
																									: q.enabled
																						})
																					)
																				})
																			)
																		})
																	}
																/>
															</div>
															<Text type="secondary" style={{ fontSize: 12 }}>
																{tAnswers("questionType")}:{" "}
																{question.type === "text"
																	? tAnswers("textType")
																	: tAnswers("optionType")}
															</Text>
														</div>
													}
													className="mb-3"
												>
													{/* {questionAnswers.length > 0 ? (
														<SurveyAnswersTable data={questionAnswers} />
													) : (
														<div className="p-3">
															<Text type="secondary">{tAnswers("noAnswers")}</Text>
														</div>
													)} */}
												</Card>
											)
										})}
									</Space>
								</Card>
							)
						})}
					</Space>
				</Col>
			</Row>
		</>
	)
}

export default observer(SurveySectionsForm)
